import { BathProductPage, BathProductConfig } from "@/components/bath/BathProductPage";
import { Rem7HydraSpecPDFButton } from "@/components/bath/Rem7HydraSpecPDFButton";
import { Rem7HydraInBoxGrid, InBoxItem } from "@/components/bath/Rem7HydraInBoxGrid";
import { Rem7HydraSpecsPanel, SpecGroup, DimensionMetric, InBoxItem as PanelInBoxItem } from "@/components/bath/Rem7HydraSpecsPanel";
import { Rem7HydraFeatureShowcase, FeatureModule } from "@/components/bath/Rem7HydraFeatureShowcase";
import {
  Sparkles, ShieldCheck, Wrench, Layers, TrendingUp, CheckCircle2,
  Home, Building2, Hotel,
  Cog, Waves, Lightbulb, Settings2, Speaker, ShowerHead, Droplets,
  Armchair, Package, Square, Anchor, Ruler, Radio,
} from "lucide-react";

/**
 * /:lang/bath-sanitary/rem7-whirlpool
 * REM7 Hydra — acrylic whirlpool massage bathtub with chromotherapy LED, dual hydro + air massage,
 * digital touch control, FM radio and tempered glass apron. Aureum VII Trading flagship spa tub.
 */
const config: BathProductConfig = {
  slug: "rem7-whirlpool",
  category: "Whirlpool Massage Bathtub",
  productName: "REM7 Hydra Acrylic Whirlpool Massage Bathtub",
  shortName: "REM7 Hydra Whirlpool",
  sku: "REM7-HYDRA-1825",
  schemaCategory: "Bathtubs > Whirlpool > Acrylic",
  metaTitle: "REM7 Hydra Whirlpool | Acrylic Massage Bathtub by Aureum VII",
  metaDescription:
    "REM7 Hydra acrylic whirlpool bathtub: dual hydro and air massage, chromotherapy LED, digital control. 1850×1250×650mm, CE certified, EU bulk supply.",
  heroEyebrow: "Project Solutions · Whirlpool Spa Bathtub",
  heroHeadline: (
    <>
      Acrylic Whirlpool{" "}
      <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 bg-clip-text text-transparent">
        Massage Bathtub
      </span>
    </>
  ),
  heroIntro:
    "Dual hydro and air massage. Chromotherapy LED. Digital touch control with FM radio and ozone disinfection. The REM7 Hydra is a hospitality-grade in-home spa bathtub engineered for premium residential and boutique hotel projects across Europe.",
  premiumSummary:
    "An 1850 mm twin-bather whirlpool bathtub built around 100% pure sanitary-grade PMMA acrylic, a 1.5HP water massage pump, 16-jet ultra-thin hydro array, 300W bubble pump and a tempered safety glass apron with #304 stainless steel armrest. Hospitality-grade specification at trade pricing.",
  galleryImages: [
    {
      src: "/images/bath/whirlpool/hero.jpg",
      alt: "REM7 Hydra acrylic whirlpool massage bathtub with chromotherapy LED in modern luxury bathroom",
      title: "REM7 Hydra Whirlpool Bathtub — Hero",
      caption: "Hero · Whirlpool with LED chromotherapy",
      description:
        "Hero of the REM7 Hydra 1850 mm whirlpool massage bathtub in white acrylic with tempered glass front apron, illuminated by colourful LED chromotherapy.",
    },
    {
      src: "/images/bath/whirlpool/angle.jpg",
      alt: "REM7 Hydra whirlpool bathtub three-quarter angle view with stainless steel armrest",
      title: "REM7 Hydra — Angle View",
      caption: "Angle · Tempered glass apron",
      description:
        "Three-quarter angle of the REM7 Hydra whirlpool bathtub showing the 8 mm tempered safety glass decorative apron and #304 stainless steel armrest detail.",
    },
    {
      src: "/images/bath/whirlpool/controls.jpg",
      alt: "REM7 Hydra digital touch control panel with water sensor ozone disinfection FM radio",
      title: "REM7 Hydra — Digital Touch Control",
      caption: "Detail · Touch control system",
      description:
        "Macro of the REM7 Hydra digital touch control system featuring water sensor, ozone disinfection, FM radio and integrated loudspeaker.",
    },
    {
      src: "/images/bath/whirlpool/jets.jpg",
      alt: "REM7 Hydra hydro massage jets ultra-thin design 4 large 12 small water jets",
      title: "REM7 Hydra — Hydro Jet Array",
      caption: "Detail · Ultra-thin hydro jets",
      description:
        "Detail of the REM7 Hydra ultra-thin hydro jet array — 4 large + 12 small water jets driven by a 1.5HP water massage pump.",
    },
    {
      src: "/images/bath/whirlpool/led.jpg",
      alt: "REM7 Hydra colourful LED bubble jet chromotherapy underwater lighting",
      title: "REM7 Hydra — Chromotherapy LED",
      caption: "Lighting · LED chromotherapy",
      description:
        "Underwater chromotherapy on the REM7 Hydra — 16 colourful LED bubble jets driven by a 300W air bubble pump for spa-grade ambience.",
    },
    {
      src: "/images/bath/whirlpool/lifestyle.jpg",
      alt: "REM7 Hydra whirlpool bathtub installed in luxury hotel suite with hand shower and mixer tap",
      title: "REM7 Hydra — Hotel Suite Lifestyle",
      caption: "Lifestyle · Hotel spa suite",
      description:
        "Lifestyle scene: REM7 Hydra whirlpool installed in a luxury hotel suite with hot/cold mixer, diverter and hand shower for guest use.",
    },
    {
      src: "/images/bath/whirlpool/side.jpg",
      alt: "REM7 Hydra whirlpool bathtub side profile with seamless decorative apron",
      title: "REM7 Hydra — Side Profile",
      caption: "Studio · Side profile",
      description:
        "Side studio profile of the REM7 Hydra whirlpool bathtub showing the 4-side seamless finished apron with decorative LED accent line.",
    },
  ],
  specs: [
    { label: "Dimensions", value: "1850 × 1250 × 650 mm (L × W × H)" },
    { label: "Capacity", value: "Twin bather · ≈ 380 L to overflow" },
    { label: "Shell Material", value: "100% Sanitary-grade PMMA Acrylic" },
    { label: "Reinforcement", value: "Alkali-free fibreglass + resin + wood reinforcement" },
    { label: "Hydro Massage", value: "1.5HP pump · 4 large + 12 small ultra-thin jets" },
    { label: "Air / Bubble Bath", value: "300W bubble pump · 16 colourful LED bubble jets" },
    { label: "Control System", value: "Digital touch · water sensor · ozone · FM radio · loudspeaker" },
    { label: "Plumbing", value: "Hot/cold mixer · diverter · hand shower · drainer with overflow" },
    { label: "Apron", value: "8 mm tempered safety glass · #304 stainless steel armrest · LED accent" },
    { label: "Pillows", value: "2 × dark grey ergonomic headrests" },
    { label: "Support / Feet", value: "Stainless steel frame · pre-levelled height-adjustable feet" },
    { label: "Certification", value: "CE · SGS · cUPC · ISO 9001" },
    { label: "Warranty", value: "5 years shell · 2 years components" },
    { label: "MOQ", value: "From 5 units · Bulk pricing 20+" },
    { label: "Lead Time", value: "20–30 working days · 40ft container ≈ 28 units" },
  ],
  whyChoose: [
    {
      icon: Sparkles,
      title: "Full spa specification in one tub",
      body: "Hydro massage, air bubble massage, chromotherapy LED, digital touch control, FM radio and ozone — a complete in-suite spa experience without bolt-on accessories.",
    },
    {
      icon: TrendingUp,
      title: "Higher ADR per hotel suite",
      body: "A whirlpool tub photographs as a destination feature. Boutique hotels and short-let operators justifiably command higher nightly rates on whirlpool-equipped rooms.",
    },
    {
      icon: ShieldCheck,
      title: "Sanitary-grade durability",
      body: "100% pure PMMA acrylic, fibreglass + resin + wood reinforcement, water-sensor protected pump, and stainless support. CE / SGS / cUPC certified for residential and hospitality.",
    },
    {
      icon: Wrench,
      title: "Specifier-friendly installation",
      body: "Standard EU plumbing rough-in, integrated drainer with overflow, and pre-levelled adjustable feet. Service access via the tempered glass apron — no surprise components on site.",
    },
    {
      icon: Layers,
      title: "Predictable bulk supply",
      body: "MOQ from 5 units, bulk pricing from 20+. ~28 units per 40ft container, 20–30 working days production — predictable for phased developer rollouts.",
    },
    {
      icon: CheckCircle2,
      title: "Margin-protective pricing",
      body: "Trade pricing structured to protect project margin — hospitality-grade whirlpool aesthetic without the European brand premium.",
    },
  ],
  perfectFor: [
    { icon: Hotel, title: "Boutique Hotels & Spas", body: "Premium suites and wellness rooms where the whirlpool is the headline amenity." },
    { icon: Home, title: "Branded Residences", body: "High-end residential and rental developments needing a signature spa bathroom." },
    { icon: Building2, title: "Private Villas", body: "Master suites, pool houses and wellness wings where a whirlpool tub anchors the brief." },
    { icon: Wrench, title: "Premium Renovations", body: "Bathroom refurbishments upgrading from a standard tub to a true in-home spa." },
  ],
  quickFeatures: [
    "1850 × 1250 × 650 mm twin-bather format",
    "1.5HP hydro pump · 16 ultra-thin water jets",
    "300W air pump · 16 chromotherapy LED bubble jets",
    "Digital touch control with FM radio & loudspeaker",
    "Ozone disinfection + water-sensor pump protection",
    "8 mm tempered glass apron · #304 stainless armrest",
    "100% sanitary-grade PMMA acrylic shell",
    "CE / SGS / cUPC · 5-yr shell warranty",
  ],
  productMaterial: "100% Sanitary-grade PMMA Acrylic with fibreglass + resin reinforcement",
  additionalProperties: [
    { name: "Length", value: "1850", unitText: "MMT" },
    { name: "Width", value: "1250", unitText: "MMT" },
    { name: "Height", value: "650", unitText: "MMT" },
    { name: "Capacity", value: "380", unitText: "LTR" },
    { name: "Hydro Pump Power", value: "1.5", unitText: "HP" },
    { name: "Air Pump Power", value: "300", unitText: "WTT" },
    { name: "Voltage", value: "220V / 50Hz" },
    { name: "Bather Capacity", value: "2" },
    { name: "Hydro Jets", value: "16 (4 large + 12 small)" },
    { name: "LED Bubble Jets", value: "16" },
    { name: "Certification", value: "CE, SGS, cUPC, ISO 9001" },
    { name: "Shell Warranty", value: "5 years" },
    { name: "Component Warranty", value: "2 years" },
  ],
  faqs: [
    {
      question: "What are the dimensions of the REM7 Hydra whirlpool bathtub?",
      answer:
        "The REM7 Hydra measures 1850 × 1250 × 650 mm (L × W × H) with a working capacity of approximately 380 litres to overflow. It is sized as a twin-bather whirlpool, suitable for couples and primary suite specification.",
    },
    {
      question: "What massage system does the REM7 Hydra use?",
      answer:
        "It runs a dual hydro + air system. A 1.5HP water massage pump drives 16 ultra-thin hydro jets (4 large + 12 small), while a separate 300W air bubble pump powers 16 colourful LED chromotherapy bubble jets — controlled independently from the digital touch panel.",
    },
    {
      question: "Is the REM7 Hydra certified for European installation?",
      answer:
        "Yes. The REM7 Hydra carries CE, SGS, cUPC and ISO 9001 certification, and is engineered around standard EU plumbing rough-in with hot/cold mixer, diverter, hand shower and integrated drainer with overflow. It is approved for residential and hospitality projects across the EU.",
    },
    {
      question: "What materials are used in the shell and apron?",
      answer:
        "The shell is cast from 100% sanitary-grade PMMA acrylic, reinforced with alkali-free fibreglass, resin and structural wood. The decorative apron is 8 mm tempered safety glass with a #304 stainless steel armrest detail and a discreet decorative LED accent line.",
    },
    {
      question: "What is the warranty on the REM7 Hydra?",
      answer:
        "Aureum VII Trading covers the REM7 Hydra with a 5-year shell warranty and a 2-year warranty on electrical and mechanical components (pumps, control unit, LED). Professional installation is required for warranty validity.",
    },
    {
      question: "What is the MOQ and lead time for project orders?",
      answer:
        "MOQ starts at 5 units, with bulk pricing tiers from 20 units. Production lead time is 20–30 working days. A 40ft container ships approximately 28 units. EU sea freight is 30–40 days; air freight 10–15 days for sample and showroom units.",
    },
    {
      question: "Does the REM7 Hydra include hygiene and safety features?",
      answer:
        "Yes. It includes an ozone disinfection cycle that sanitises water and pipework between uses, a water-sensor circuit that prevents the hydro pump from running dry, and a tempered safety glass apron with stainless armrest. The pre-levelled adjustable feet and sealed electronics meet hospitality safety expectations.",
    },
    {
      question: "Can the REM7 Hydra be specified for hotels and short-let developments?",
      answer:
        "It is designed for exactly that use case. Boutique hotels, branded residences, serviced apartments and short-let operators specify the REM7 Hydra as a destination amenity that lifts ADR on whirlpool-equipped suites and improves marketing photography across the room category.",
    },
  ],
  longDescription: [
    "The REM7 Hydra is an acrylic whirlpool massage bathtub engineered for the way premium European bathrooms are actually specified, installed and used. It pairs a hospitality-grade dual hydro + air massage system with a digital touch control, chromotherapy LED, FM radio and ozone disinfection — a full in-suite spa experience inside a single 1850 × 1250 × 650 mm twin-bather format.",
    "The shell is cast from 100% sanitary-grade PMMA acrylic, reinforced with alkali-free fibreglass, resin and structural wood. A 1.5HP water massage pump drives an ultra-thin jet array (4 large + 12 small), while a 300W air pump powers 16 colourful LED bubble jets for chromotherapy. A water-sensor circuit protects the pump from running dry, and an ozone disinfection cycle keeps the water and pipework hygienic between guests.",
    "For developers and hotel operators, the math is simple: a whirlpool tub photographs as a destination amenity, drives higher ADR on the suites it occupies, and lifts marketing photography across the entire room category. The REM7 Hydra is sourced specifically to deliver that photograph — and that ADR uplift — without a European luxury-brand price tag.",
    "Specifier-friendly installation was a non-negotiable. Standard EU plumbing rough-in, hot/cold mixer, diverter and hand shower, integrated drainer with overflow, and pre-levelled stainless steel adjustable feet. Service access is provided through the 8 mm tempered safety glass decorative apron, which also carries a #304 stainless steel armrest and a discreet decorative LED accent.",
    "The REM7 Hydra completes the Aureum VII bath programme alongside the ARIA Sculpt, ARIA Monolith, ARIA Diamond, VENA basin and vanity range, and the REM7 smart toilet flagship — a coherent specification package for branded residences, boutique hotels, and signature private villas across Europe.",
  ],
};

const inBoxItems: InBoxItem[] = [
  { icon: Square, label: "Acrylic whirlpool shell", qty: "1 × 1850 × 1250 × 650 mm", note: "100% sanitary-grade PMMA · fibreglass + resin + wood reinforced" },
  { icon: Settings2, label: "Digital touch control panel", qty: "1 unit · pre-installed", note: "Water sensor · ozone disinfection · FM radio · loudspeaker" },
  { icon: Cog, label: "Hydro massage pump", qty: "1 × 1.5 HP", note: "Run-without-water protection circuit" },
  { icon: Waves, label: "Ultra-thin hydro jets", qty: "16 jets · 4 large + 12 small", note: "Back, lumbar and leg coverage" },
  { icon: Sparkles, label: "Air bubble pump", qty: "1 × 300 W", note: "Independent of hydro circuit" },
  { icon: Lightbulb, label: "Chromotherapy LED bubble jets", qty: "16 LEDs · floor-mounted", note: "Multi-colour underwater lighting" },
  { icon: ShowerHead, label: "Hot/cold mixer + diverter mixer", qty: "1 set", note: "Brushed chrome finish" },
  { icon: ShowerHead, label: "Hand shower with hose", qty: "1 unit", note: "Connects to diverter outlet" },
  { icon: Droplets, label: "Water intake faucet", qty: "1 unit", note: "High-flow filling spout" },
  { icon: Package, label: "Backwater drainer with overflow", qty: "1 set · pre-fitted", note: "Integrated overflow channel" },
  { icon: Armchair, label: "Ergonomic headrest pillows", qty: "2 pcs · dark grey", note: "Removable for cleaning" },
  { icon: Square, label: "Tempered glass decorative apron", qty: "1 × 8 mm panel", note: "With decorative LED accent line" },
  { icon: Anchor, label: "#304 stainless steel armrest", qty: "1 unit · pre-installed", note: "Anti-corrosion finish" },
  { icon: Wrench, label: "Stainless steel support frame", qty: "1 set", note: "Pre-levelled · height-adjustable feet" },
  { icon: Speaker, label: "FM radio + loudspeaker module", qty: "1 unit · integrated", note: "Wired to touch control panel" },
  { icon: Package, label: "Installation & warranty pack", qty: "1 set", note: "Manual · CE certificate · 5/2-yr warranty card" },
];

const dimensionMetrics: DimensionMetric[] = [
  { label: "Length", value: "1850", unit: "mm" },
  { label: "Width", value: "1250", unit: "mm" },
  { label: "Height", value: "650", unit: "mm" },
  { label: "Capacity", value: "380", unit: "Litres" },
];

const specGroups: SpecGroup[] = [
  {
    id: "dimensions",
    icon: Ruler,
    title: "Dimensions & Format",
    rows: [
      { label: "Overall Size", value: "1850 × 1250 × 650 mm" },
      { label: "Format", value: "Rectangular · twin bather" },
      { label: "Capacity", value: "≈ 380 L to overflow" },
      { label: "Apron", value: "8 mm tempered glass · 4-side finish" },
      { label: "Net Weight (approx.)", value: "≈ 130 kg" },
    ],
  },
  {
    id: "hydromassage",
    icon: Waves,
    title: "Hydro Massage System",
    rows: [
      { label: "Hydro Pump", value: "1.5 HP water massage pump" },
      { label: "Hydro Jets", value: "16 ultra-thin (4 large + 12 small)" },
      { label: "Pump Protection", value: "Water-sensor cut-off circuit" },
      { label: "Massage Coverage", value: "Back · lumbar · legs · feet" },
    ],
  },
  {
    id: "bubble",
    icon: Sparkles,
    title: "Air Bubble & LED",
    rows: [
      { label: "Air Pump", value: "300 W bubble pump" },
      { label: "LED Bubble Jets", value: "16 chromotherapy LEDs" },
      { label: "Operating Mode", value: "Independent of hydro circuit" },
      { label: "Lighting", value: "Multi-colour chromotherapy" },
    ],
  },
  {
    id: "control",
    icon: Radio,
    title: "Digital Touch Control",
    rows: [
      { label: "Interface", value: "Digital touch panel" },
      { label: "Water Sensor", value: "Yes · safe-operation lockout" },
      { label: "Disinfection", value: "Ozone module on recirculation" },
      { label: "Audio", value: "FM radio + integrated loudspeaker" },
      { label: "Power Supply", value: "220 V / 50 Hz" },
    ],
  },
  {
    id: "plumbing",
    icon: Droplets,
    title: "Plumbing & Hardware",
    rows: [
      { label: "Mixer", value: "Hot/cold mixer + diverter mixer" },
      { label: "Hand Shower", value: "Included with hose" },
      { label: "Drain", value: "Backwater drainer with overflow" },
      { label: "Apron Detail", value: "8 mm tempered safety glass + LED accent" },
      { label: "Armrest", value: "#304 stainless steel armrest" },
    ],
  },
  {
    id: "build",
    icon: ShieldCheck,
    title: "Build, Certification & Logistics",
    rows: [
      { label: "Shell Material", value: "100% sanitary-grade PMMA acrylic" },
      { label: "Reinforcement", value: "Fibreglass + resin + wood" },
      { label: "Support Frame", value: "Stainless steel · adjustable feet" },
      { label: "Certification", value: "CE · SGS · cUPC · ISO 9001" },
      { label: "Warranty", value: "5 yrs shell · 2 yrs components" },
      { label: "MOQ / Container", value: "From 5 units · ≈ 28 / 40ft" },
      { label: "Lead Time", value: "20–30 working days" },
    ],
  },
];

const panelInBoxItems: PanelInBoxItem[] = inBoxItems.map(({ icon, label, qty }) => ({ icon, label, qty }));

const featureModules: FeatureModule[] = [
  {
    id: "control",
    icon: Radio,
    label: "Touch Console",
    tagline: "Module 01 · Control",
    title: "Digital touch console with ozone, FM radio & loudspeaker",
    description:
      "A single digital touch panel runs every system on the tub — whirlpool, air bubble and lighting — with built-in water sensor, ozone disinfection, FM radio and integrated loudspeaker for a true spa-audio experience.",
    bullets: [
      "Digital touch interface · ultra-thin glass surface",
      "Water sensor for safe operation and dry-run protection",
      "Ozone disinfection module on the recirculation loop",
      "Built-in FM radio + loudspeaker for in-tub audio",
    ],
    image: { src: "/images/bath/whirlpool/controls.jpg", alt: "REM7 Hydra digital touch control panel" },
    metric: { value: "1-Touch", label: "Unified Spa Control" },
  },
  {
    id: "hydro",
    icon: Waves,
    label: "Hydro Massage",
    tagline: "Module 02 · Hydro Massage",
    title: "1.5HP hydro massage with 16 ultra-thin jets",
    description:
      "A 1.5HP whirlpool pump drives a dual-zone hydro massage through 4 large + 12 small ultra-thin jets, configured for back, lumbar, leg and foot coverage. Water-sensor cut-off protects the pump from running dry.",
    bullets: [
      "1.5HP whirlpool pump · water-sensor cut-off circuit",
      "16 ultra-thin water jets (4 large + 12 small)",
      "Adjustable air whirlpool switch for massage intensity",
      "Back / lumbar / leg / foot coverage in twin-bather format",
    ],
    image: { src: "/images/bath/whirlpool/jets.jpg", alt: "REM7 Hydra hydro massage jet array" },
    metric: { value: "16 Jets", label: "1.5HP Hydro Massage" },
  },
  {
    id: "led",
    icon: Sparkles,
    label: "LED Bubble",
    tagline: "Module 03 · Bubble Bath",
    title: "300W bubble bath with 16 chromotherapy LED jets",
    description:
      "A separate 300W air pump powers 16 colourful LED bubble jets for chromotherapy, controlled independently from the touch panel — a soft bubble bath with mood lighting, without engaging the high-pressure hydro massage.",
    bullets: [
      "300W dedicated air pump · whisper-quiet operation",
      "16 chromotherapy LED bubble jets · floor-mounted",
      "Independent control from the hydro massage circuit",
      "Soft bath / mood lighting mode for evening use",
    ],
    image: { src: "/images/bath/whirlpool/led.jpg", alt: "REM7 Hydra chromotherapy LED bubble jets" },
    metric: { value: "16 LEDs", label: "Chromotherapy Bath" },
  },
  {
    id: "apron",
    icon: Square,
    label: "Glass Apron",
    tagline: "Module 04 · Aesthetic",
    title: "Tempered glass apron with stainless armrest",
    description:
      "A 8 mm tempered safety glass decorative apron carries a discreet LED accent line and a #304 stainless steel armrest — service access through the apron keeps installation clean and on-site work straightforward.",
    bullets: [
      "8 mm tempered safety glass decorative apron",
      "#304 stainless steel armrest · anti-corrosion finish",
      "Discreet decorative LED accent line",
      "Service access for pumps and electronics",
    ],
    image: { src: "/images/bath/whirlpool/angle.jpg", alt: "REM7 Hydra tempered glass apron with stainless armrest" },
    metric: { value: "8 mm", label: "Tempered Safety Glass" },
  },
  {
    id: "plumbing",
    icon: Wrench,
    label: "Plumbing Pack",
    tagline: "Module 05 · Plumbing",
    title: "Complete EU plumbing package out-of-the-box",
    description:
      "Every REM7 Hydra ships with a complete European plumbing pack — hot/cold mixer, diverter, hand shower, drainer with overflow — so the trade can install with standard rough-in and no exotic parts.",
    bullets: [
      "Hot / cold mixer + diverter mixer included",
      "Hand shower with hose · brushed chrome",
      "Backwater drainer with integrated overflow",
      "Pre-levelled, height-adjustable stainless feet",
    ],
    image: { src: "/images/bath/whirlpool/lifestyle.jpg", alt: "REM7 Hydra plumbing pack with mixer and hand shower" },
    metric: { value: "EU-Ready", label: "Standard Rough-In" },
  },
  {
    id: "build",
    icon: ShieldCheck,
    label: "Build Quality",
    tagline: "Module 06 · Materials",
    title: "Sanitary-grade PMMA acrylic on stainless frame",
    description:
      "100% pure sanitary-grade PMMA acrylic shell — high-gloss, scratch / stain / fade resistant — reinforced with alkali-free fibreglass, resin and structural wood, mounted on a stainless steel frame with pre-levelled feet and CE / SGS / cUPC certification.",
    bullets: [
      "100% sanitary-grade PMMA acrylic shell",
      "Fibreglass + resin + wood reinforcement layers",
      "Stainless steel structural support frame",
      "CE · SGS · cUPC · ISO 9001 certified",
    ],
    image: { src: "/images/bath/whirlpool/side.jpg", alt: "REM7 Hydra acrylic shell side profile" },
    metric: { value: "5-Year", label: "Shell Warranty" },
  },
];

const Rem7Whirlpool = () => (
  <BathProductPage
    config={{
      ...config,
      specsPdfButton: (
        <Rem7HydraSpecPDFButton specs={config.specs} features={config.quickFeatures ?? []} />
      ),
      featureShowcase: (
        <>
          <Rem7HydraFeatureShowcase shortName={config.shortName} modules={featureModules} />
          <Rem7HydraInBoxGrid shortName={config.shortName} items={inBoxItems} />
        </>
      ),
      specsPanel: (
        <Rem7HydraSpecsPanel
          shortName={config.shortName}
          productName={config.productName}
          sku={config.sku}
          heroImage={{
            src: "/images/bath/whirlpool/angle.jpg",
            alt: "REM7 Hydra whirlpool dimensional reference view",
          }}
          dimensions={dimensionMetrics}
          groups={specGroups}
          inBox={panelInBoxItems}
          certifications={["CE", "SGS", "cUPC", "ISO 9001"]}
          pdfButton={
            <Rem7HydraSpecPDFButton specs={config.specs} features={config.quickFeatures ?? []} />
          }
        />
      ),
    }}
  />
);
export default Rem7Whirlpool;
